import { useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { useTranslation } from 'react-i18next';
import { type DigitalBudgetTableRowData } from 'pages/calculator/MediaPlanning/common/BudgetTable/interfaces';
import { formatNumberWithSpaces } from 'pages/calculator/MediaPlanning/common/BudgetTable/utils/formatNumberWithSpaces';
import {
  Box,
  Collapse,
  IconButton,
  SxProps,
  TableCell,
  TableRow,
  Typography,
} from '@mui/material';
import NestedBudgetTable from 'pages/calculator/MediaPlanning/common/BudgetTable/DigitalBudgetTable/NestedBudgetTable/NestedBudgetTable';
import convertDecimalToPercentValue from '../../../../Plan/Countries/utils/convertDecimalToPercentValue';
import { type GoldenRuleMap } from '../DigitalBudgetTable';

const GR_TOUCHPOINTS = ['META', 'YOUTUBE', 'TIKTOK'];
const GR_COLOR_NA = '#525F81';

interface BudgetTableRowProps {
  rowData: DigitalBudgetTableRowData;
  isTotalBudgetRow?: boolean;
  sx?: SxProps;
  goldenRuleMap?: GoldenRuleMap;
  goldenRuleAlign?: 'left' | 'right';
}

function BudgetTableRow({
  rowData,
  isTotalBudgetRow,
  sx,
  goldenRuleMap,
  goldenRuleAlign = 'left',
}: BudgetTableRowProps) {
  const [isRowOpen, setIsRowOpen] = useState(false);
  const { t } = useTranslation();

  const renderGoldenRuleCell = () => {
    const dash = (
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px' }}>
        -
      </Typography>
    );

    if (
      !goldenRuleMap ||
      isTotalBudgetRow ||
      !GR_TOUCHPOINTS.includes(rowData.touchpointName)
    ) {
      return dash;
    }

    const grData = goldenRuleMap[rowData.touchpointName];
    if (!grData) return dash;

    if (grData.isGoldenRuleMet === false) {
      return (
        <Typography
          variant="body2"
          sx={{ fontWeight: 700, fontSize: '14px', color: GR_COLOR_NA }}
        >
          N/A
        </Typography>
      );
    }

    if (!grData.grReach || !grData.reach) return dash;

    const percentage =
      Math.round((grData.reach / grData.grReach) * 10000) / 100;

    return (
      <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '14px' }}>
        {percentage}%
      </Typography>
    );
  };

  if (!rowData.children.length) {
    return null;
  }

  return (
    <>
      <TableRow sx={{ borderBottom: isRowOpen ? 'none' : '1px solid', ...sx }}>
        <TableCell>
          <IconButton
            size="small"
            sx={{
              color: 'common.black',
              position: 'relative',
              zIndex: 2,
              pointerEvents: 'auto',
            }}
            onClick={() => setIsRowOpen(!isRowOpen)}
          >
            {isRowOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
        <TableCell align="left">
          <Typography
            variant="body2"
            fontWeight={700}
            textTransform="uppercase"
          >
            {t(rowData.touchpointName)}
          </Typography>
        </TableCell>
        {goldenRuleMap && (
          <TableCell align={goldenRuleAlign}>
            {renderGoldenRuleCell()}
          </TableCell>
        )}
        <TableCell />
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {formatNumberWithSpaces(rowData.impressions)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {formatNumberWithSpaces(rowData.reach)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {formatNumberWithSpaces(rowData.clicks)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {formatNumberWithSpaces(rowData.videoViews)}
          </Typography>
        </TableCell>
        <TableCell align="right">
          <Typography variant="body2" fontWeight={700}>
            {`${formatNumberWithSpaces(rowData.budget.value?.toFixed(0))} ${
              rowData.budget.currency
            }`}
          </Typography>
        </TableCell>
      </TableRow>
      <TableRow sx={{ borderBottom: isRowOpen ? '1px solid' : 'none', ...sx }}>
        <TableCell style={{ padding: 0 }} colSpan={goldenRuleMap ? 9 : 8}>
          <Collapse in={isRowOpen} unmountOnExit>
            <Box sx={{ padding: '16px 0' }}>
              <NestedBudgetTable
                rowData={rowData.children}
                isTotalBudgetRow={isTotalBudgetRow}
              />
            </Box>
          </Collapse>
        </TableCell>
      </TableRow>
    </>
  );
}

export default BudgetTableRow;
